import { TelegramLoginLinkInput } from '@lub0v/santa-utils';
import type { OtpVerifyFailure } from '@src/components/OtpChallenge/OtpChallenge';
import { isInAppHref } from '@src/routing/href';
import type { NavigateFunction } from 'react-router-dom';
import { santaClient } from '@src/state/client';
import { loadSession } from '@src/state/sessionSlice';
import { store } from '@src/state/store';
import { loadTelegramWidget } from '@src/utils/telegramWidget';

function redirectTo(navigate: NavigateFunction, url: string | undefined, fallback = '/account/boxes') {
  const target = url || fallback;
  if (isInAppHref(target)) {
    void store.dispatch(loadSession()).then(() => navigate(target));
    return;
  }
  window.location.assign(target);
}

export async function loginWithEmail(navigate: NavigateFunction, email: string, password: string): Promise<string | null> {
  const res = await santaClient.login({ email, password, redirect: '/' });
  if (res.ok) {
    redirectTo(navigate, '/account/boxes');
    return null;
  }
  return res.error?.message || 'login.failed';
}

export async function loginWithGoogle(navigate: NavigateFunction): Promise<string | null> {
  const res = await santaClient.loginGoogle({ login: true, redirect: '/' });
  if (res.ok && res.data?.redirect) {
    redirectTo(navigate, res.data.redirect);
    return null;
  }
  return res.error?.message || 'login.failed';
}

export async function loginWithVk(navigate: NavigateFunction): Promise<string | null> {
  const res = await santaClient.loginVkontakte({ login: true, redirect: '/' });
  if (res.ok && res.data?.redirect) {
    redirectTo(navigate, res.data.redirect);
    return null;
  }
  return res.error?.message || 'login.failed';
}

/** Link a social account to the signed-in profile. OAuth is a real load. */
export async function linkWithGoogle(): Promise<string | null> {
  const res = await santaClient.loginGoogle({ login: false, redirect: '/account' });
  if (res.ok && res.data?.redirect) {
    window.location.assign(res.data.redirect);
    return null;
  }
  return res.error?.message || 'login.failed';
}

export async function linkWithVk(): Promise<string | null> {
  const res = await santaClient.loginVkontakte({ login: false, redirect: '/account' });
  if (res.ok && res.data?.redirect) {
    window.location.assign(res.data.redirect);
    return null;
  }
  return res.error?.message || 'login.failed';
}

export async function linkWithTelegram(): Promise<string | null> {
  try {
    await loadTelegramWidget();
  } catch {
    return 'login.telegramUnavailable';
  }
  if (!window.Telegram?.Login?.auth || !window.TelegramBotId) {
    return 'login.telegramUnavailable';
  }

  return new Promise((resolve) => {
    window.Telegram?.Login.auth(
      { bot_id: window.TelegramBotId as string, request_access: true },
      async (data) => {
        if (!data) {
          resolve('login.failed');
          return;
        }
        const res = await santaClient.loginTelegram({
          login: false,
          redirect: '/account',
          ...data,
        } as TelegramLoginLinkInput);
        if (res.ok && res.data?.redirect) {
          window.location.assign(res.data.redirect);
          resolve(null);
          return;
        }
        resolve(res.error?.message || 'login.failed');
      },
    );
  });
}

export async function loginWithTelegram(navigate: NavigateFunction): Promise<string | null> {
  try {
    await loadTelegramWidget();
  } catch {
    return 'login.telegramUnavailable';
  }
  if (!window.Telegram?.Login?.auth || !window.TelegramBotId) {
    return 'login.telegramUnavailable';
  }

  return new Promise((resolve) => {
    window.Telegram?.Login.auth(
      { bot_id: window.TelegramBotId as string, request_access: true },
      async (data) => {
        if (!data) {
          resolve('login.failed');
          return;
        }
        const res = await santaClient.loginTelegram({
          login: true,
          redirect: '/',
          ...data,
        } as TelegramLoginLinkInput);
        if (res.ok && res.data?.redirect) {
          redirectTo(navigate, res.data.redirect);
          resolve(null);
          return;
        }
        resolve(res.error?.message || 'login.failed');
      },
    );
  });
}

export async function registerWithEmail(
  email: string,
): Promise<{ token: string } | { error: string }> {
  const res = await santaClient.register({ email, redirect: '/' });
  if (res.ok && res.data?.challengeToken) {
    return { token: res.data.challengeToken };
  }
  if (res.ok) {
    return { error: 'register.failed' };
  }
  return { error: res.error?.message || 'register.failed' };
}

export async function recoverWithEmail(
  email: string,
): Promise<{ token: string; isAccount: boolean } | { error: string }> {
  const res = await santaClient.recover({ email, redirect: '/' });
  if (res.ok && res.data?.challengeToken) {
    return { token: res.data.challengeToken, isAccount: Boolean(res.data.isAccount) };
  }
  if (res.ok) {
    return { error: 'recover.failed' };
  }
  return { error: res.error?.message || 'recover.failed' };
}

function attemptsLeftFromError(error: { data?: { attemptsLeft?: unknown } } | undefined): number | undefined {
  const value = error?.data?.attemptsLeft;
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : undefined;
}

export async function verifyOtpCode(
  navigate: NavigateFunction,
  challengeToken: string,
  code: string,
): Promise<OtpVerifyFailure | null> {
  const res = await santaClient.verifyNotificationCode({ challengeToken, code });
  if (res.ok && res.data?.redirect) {
    redirectTo(navigate, res.data.redirect);
    return null;
  }
  const message = res.error?.message;
  if (message === 'NOTIFICATION_ACCESS_CODE_LOCKED') {
    return { key: 'otp.locked' };
  }
  if (message === 'NOTIFICATION_ACCESS_CODE_WRONG') {
    return { key: 'otp.wrong', attemptsLeft: attemptsLeftFromError(res.error) };
  }
  if (message === 'Too many requests' || res.error?.status === 429) {
    return { key: 'otp.rateLimited' };
  }
  return { key: message || 'otp.wrong' };
}

export async function recoverWithCode(
  navigate: NavigateFunction,
  email: string,
  code: string,
): Promise<string | null> {
  const response = await fetch('/api/login/recovery-code', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  });
  if (response.ok) {
    redirectTo(navigate, '/account');
    return null;
  }
  if (response.status === 429) return 'otp.rateLimited';
  return response.status >= 500 ? 'recover.failed' : 'recover.codeWrong';
}
